"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const tslib_1 = require("tslib");
const Tracking_1 = tslib_1.__importDefault(require("./Tracking"));
Tracking_1.default.setupSentryUncaughtErrors();
function buildHeaders(options) {
    const headers = new Headers(options.headers);
    if (!headers.has('Content-Type') && !(options.body instanceof FormData)) {
        headers.set('Content-Type', 'application/json');
    }
    if (options.newErrors) {
        headers.set('X-Errors-Format', 'v2');
    }
    if (options.useAuth && Http.isUserAuthenticated()) {
        headers.set('Authorization', `Bearer ${Http.accessToken}`);
    }
    return headers;
}
function getResponseData(response, options = {}) {
    var _a;
    const contentType = (_a = response.headers) === null || _a === void 0 ? void 0 : _a.get('Content-Type');
    const isJsonResponse = contentType === null || contentType === void 0 ? void 0 : contentType.includes('application/json');
    if (options.getFullResponse) {
        return response;
    }
    return isJsonResponse ? response.json() : response.text();
}
function fetchRequest(url, options) {
    return tslib_1.__awaiter(this, void 0, void 0, function* () {
        const { retried } = options;
        const fetchOptions = Object.assign({}, options);
        delete fetchOptions.useAuth;
        delete fetchOptions.newErrors;
        delete fetchOptions.retried;
        const response = yield fetch(url, fetchOptions);
        if (!response.ok) {
            const retryRequest = !retried && options.useAuth && [401, 403].includes(response.status);
            if (retryRequest) {
                yield Http.handleTokenExpired();
                const updatedOptions = Object.assign(Object.assign({}, options), { retried: true, 
                    // Make sure headers are up to date with new token
                    headers: buildHeaders(options) });
                return fetchRequest(url, updatedOptions);
            }
            yield Http.handleResponseError(response, options);
            throw response;
        }
        return getResponseData(response, options);
    });
}
function getAccessTokenPayload() {
    const encodedPayload = Http.accessToken.split('.')[1] || '';
    const payloadToParse = atob(encodedPayload) || '{}';
    return JSON.parse(payloadToParse);
}
/**
 * Check if existing JWT token has expired
 * @returns boolean
 */
function isTokenExpired() {
    return getAccessTokenPayload().exp * 1000 < Date.now();
}
/**
 * Check if user is authenticated by checking if token is currently set
 * @returns boolean
 */
function isUserAuthenticated() {
    return !!Http.accessToken;
}
/**
 * Wrapper around fetch to allow to make HTTP request
 * Authorised header is set for all request by default if token is set
 * Successful responses are parsed to JSON or text automatically
 * Failed request behavior can be expanded via callbacks (See registerCallbacks)
 * Failed request can be retried a few times - set number of retried via config
 * @param url
 * @param options
 * @returns Promise
 */
function request(url, options = { useAuth: true }) {
    return tslib_1.__awaiter(this, void 0, void 0, function* () {
        if (options.useAuth === undefined) {
            options.useAuth = true;
        }
        if (options.useAuth && Http.isUserAuthenticated() && Http.isTokenExpired()) {
            yield Http.handleTokenExpired();
        }
        const commonOptions = {
            headers: buildHeaders(options)
        };
        const fetchOptions = Object.assign(Object.assign({}, options), commonOptions);
        return fetchRequest(url, fetchOptions);
    });
}
/**
 * Old function used to make authorised request only
 * Deprecated in favor of request function with useAuth option. It will be removed in a future release
 *
 * @param url
 * @param options
 * @returns Promise
 * @deprecated
 */
function authorisedRequest(url, options = {}) {
    return tslib_1.__awaiter(this, void 0, void 0, function* () {
        const defaultOptions = {
            useAuth: true
        };
        const fetchOptions = Object.assign(Object.assign({}, defaultOptions), options);
        return request(url, fetchOptions);
    });
}
/**
 * Wrapper around get request - will parse parameters object to a string for get params
 *
 * @param apiUrl domain of the API that needs to be called
 * @param endpoint endpoint URL
 * @param data get parameters to pass to the request as an object
 * @returns Promise
 */
function get(apiUrl, endpoint, data, options) {
    // Replace first slash and last slash if endpoint string starts or end with /
    const parsedEndpointText = endpoint.replace(/^\/|\/$/g, '');
    // Do not do calls with trailing slash
    let url = parsedEndpointText ? `${apiUrl}/${parsedEndpointText}` : apiUrl;
    if (data) {
        const params = new URLSearchParams(data).toString();
        url = `${url}?${params}`;
    }
    return Http.request(url, options);
}
function defaultHandleResponseError(
/* eslint-disable @typescript-eslint/no-unused-vars */ // TS can't infer the correct type in registerCallbacks
response, options
/* eslint-enable @typescript-eslint/no-unused-vars */
) {
    return tslib_1.__awaiter(this, void 0, void 0, function* () {
        // Do nothing
    });
}
function defaultHandleTokenExpired() {
    return Promise.reject(new Error('token expired'));
}
/**
 * Register callbacks used to expand request behavior by intercepting requests
 * Set behaviors for
 * - Token expired
 * - Failed request
 *
 * @param callbacks
 */
function registerCallbacks(callbacks) {
    Http.handleResponseError =
        callbacks.handleResponseError || defaultHandleResponseError;
    Http.handleTokenExpired =
        callbacks.handleTokenExpired || defaultHandleTokenExpired;
}
const Http = {
    accessToken: '',
    isUserAuthenticated,
    isTokenExpired,
    request,
    authorisedRequest,
    get,
    handleResponseError: defaultHandleResponseError,
    handleTokenExpired: defaultHandleTokenExpired,
    registerCallbacks
};
exports.default = Http;
//# sourceMappingURL=Http.js.map